import type { LtvSummary, LtvSummaryCard, LtvInteraction } from '@/types/ltv';
import { num, money, dec, pct } from './shared';
import { Sparkline } from './Sparkline';

function Card({ label, card, subtitle, showDelta = true, loading, money: asMoney }: {
    label: string;
    card?: LtvSummaryCard;
    subtitle?: string;
    showDelta?: boolean;
    loading?: boolean;
    money?: boolean;
}) {
    const value = card?.value ?? 0;
    const delta = card?.delta_pct ?? 0;
    const up = delta > 0;
    const down = delta < 0;
    const color = up ? '#10b981' : '#94a3b8';
    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between min-h-[128px]">
            <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
                {loading ? (
                    <div className="mt-2 h-8 w-24 rounded-md bg-gray-100 animate-pulse" />
                ) : (
                    <div className="flex items-baseline gap-2 mt-1">
                        <span className={`font-bold text-gray-900 tracking-tight ${asMoney ? 'text-2xl' : 'text-3xl'}`}>{asMoney ? money(value) : num(value)}</span>
                        {showDelta ? (
                            <span className={`text-xs font-semibold ${up ? 'text-emerald-600' : down ? 'text-red-500' : 'text-gray-400'}`}>
                                {pct(delta)}
                            </span>
                        ) : (
                            subtitle && <span className="text-xs text-gray-400">{subtitle}</span>
                        )}
                    </div>
                )}
            </div>
            <div className="mt-3 -mb-1">
                {loading ? (
                    <div className="h-[30px] w-full rounded-md bg-gray-50 animate-pulse" />
                ) : (
                    <Sparkline data={card?.series} width={220} height={30} color={color} />
                )}
            </div>
        </div>
    );
}

export function KpiCards({ summary, isLoading, isFetching, interaction, interactionLoading }: {
    summary?: LtvSummary; isLoading?: boolean; isFetching?: boolean;
    interaction?: LtvInteraction; interactionLoading?: boolean;
}) {
    const loading = !!isLoading && !summary;
    return (
        <div className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 transition-opacity ${isFetching && summary ? 'opacity-50' : ''}`}>
            <Card label="Total de leads" card={summary?.cards.leads} loading={loading} />
            <Card label="LTV médio" card={summary?.ltv_medio} loading={loading} money />
            <Card label="Clicks" card={summary?.cards.clicks} loading={loading} />
            <Card label="Pageviews" card={summary?.cards.pageviews} loading={loading} />
            <Card label="Total de páginas" card={summary?.cards.pages} showDelta={false} subtitle="com receita no período" loading={loading} />
            <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col justify-between min-h-[128px]">
                <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Interação média</p>
                    {interactionLoading && !interaction ? (
                        <div className="mt-2 h-8 w-20 rounded-md bg-gray-100 animate-pulse" />
                    ) : (
                        <div className="flex items-baseline gap-1.5 mt-1">
                            <span className="text-3xl font-bold text-gray-900 tracking-tight">{dec(interaction?.avg_active_days ?? 0)}</span>
                            <span className="text-sm text-gray-400">dias</span>
                        </div>
                    )}
                </div>
                <p className="text-[11px] text-gray-400 mt-3">dias ativos / lead (all-time){interaction?.sampled ? ' · amostra' : ''}</p>
            </div>
        </div>
    );
}
